import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form } from 'tea-component';
import { DBOM_POINT } from '@opm/operationLog/buriedPoint';
import { TOptionsItem } from '@opm/components/FilterCommon/types';
import useIntls from '@opm/hooks/useIntls';
import {
  BURIED_CHANNEL,
  DEFAULT_QUICK_KEYS,
  getCountriesFromQuickKeys,
  TCountryMode,
  TXDimension,
} from './constants';
import { loadCountryPrefs, saveCountryPrefs } from './utils';
import {
  getInitialActiveQuickKeys,
  getInitialCountryMode,
  getInitialCustomCountries,
  getMetricsConstraints,
} from './subFilterUtils';
import type { TDateFilter } from './subFilterUtils';
import type { IXHashtagSubFilter } from './types';
import XCountryFilter from './XCountryFilter';
import XDimensionSegment from './XDimensionSegment';

export interface IXHashtagToolbarProps {
  dateFilter: TDateFilter;
  subFilter: IXHashtagSubFilter;
  setSubFilter: React.Dispatch<React.SetStateAction<IXHashtagSubFilter>>;
  countryOptions: TOptionsItem[];
}

export default function XHashtagToolbar({
  dateFilter,
  subFilter,
  setSubFilter,
  countryOptions,
}: IXHashtagToolbarProps) {
  const { intl } = useIntls();

  const [countryMode, setCountryMode] = useState<TCountryMode>(getInitialCountryMode);
  const [activeQuickKeys, setActiveQuickKeys] = useState<string[]>(
    getInitialActiveQuickKeys,
  );
  const [customCountries, setCustomCountries] = useState<string[]>(
    getInitialCustomCountries,
  );

  const { canByDate, canByCountry, forcedGroupBy } = useMemo(
    () => getMetricsConstraints(subFilter, dateFilter),
    [subFilter.countries, dateFilter],
  );

  const labels = useMemo(
    () => ({
      region: intl('Choose Region/Country'),
      metrics: intl('Dimension'),
    }),
    [intl],
  );

  useEffect(() => {
    if (!forcedGroupBy) return;
    setSubFilter((prev) =>
      prev.group_by === forcedGroupBy
        ? prev
        : { ...prev, group_by: forcedGroupBy },
    );
  }, [forcedGroupBy, setSubFilter]);

  const handleQuickSelect = useCallback(
    (nextQuickKeys: string[]) => {
      setCountryMode('quick');
      setActiveQuickKeys(nextQuickKeys);
      setSubFilter((prev) => ({
        ...prev,
        countries: getCountriesFromQuickKeys(nextQuickKeys),
      }));
      saveCountryPrefs({
        countryMode: 'quick',
        activeQuickKeys: nextQuickKeys,
        customCountries,
      });
    },
    [setSubFilter, customCountries],
  );

  const handleCustomOpen = useCallback(() => {
    setCountryMode('custom');
  }, []);

  const handleCustomCommit = useCallback(
    (countries: string[]) => {
      let quickKeysToSave = activeQuickKeys;
      if (quickKeysToSave.length === 0) {
        const savedQuickKeys = loadCountryPrefs()?.activeQuickKeys;
        quickKeysToSave =
          savedQuickKeys?.length ? savedQuickKeys : [...DEFAULT_QUICK_KEYS];
      }

      setCustomCountries(countries);
      setCountryMode('custom');
      setSubFilter((prev) => ({ ...prev, countries }));
      saveCountryPrefs({
        countryMode: 'custom',
        activeQuickKeys: quickKeysToSave,
        customCountries: countries,
      });
    },
    [setSubFilter, activeQuickKeys],
  );

  const handleDimensionChange = useCallback(
    (group_by: TXDimension) => {
      if (!canByDate && group_by === 'date') return;
      if (!canByCountry && group_by === 'country') return;

      DBOM_POINT.btn('x_dimension_switch', {
        channel: BURIED_CHANNEL,
        dimension: group_by,
      });
      setSubFilter((prev) => ({ ...prev, group_by }));
    },
    [canByDate, canByCountry, setSubFilter],
  );

  return (
    <Form className="dbom-x-hashtag-toolbar">
      <Form.Item label={labels.region} align="middle">
        <XCountryFilter
          countryMode={countryMode}
          activeQuickKeys={activeQuickKeys}
          customCountries={customCountries}
          countryOptions={countryOptions}
          onQuickSelect={handleQuickSelect}
          onCustomOpen={handleCustomOpen}
          onCustomCommit={handleCustomCommit}
        />
      </Form.Item>
      <Form.Item label={labels.metrics} align="middle">
        <XDimensionSegment
          value={subFilter.group_by}
          canByDate={canByDate}
          canByCountry={canByCountry}
          visible
          onChange={handleDimensionChange}
        />
      </Form.Item>
    </Form>
  );
}
